import React, { useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { CompanionModal } from '../../components/CompanionModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Companion } from '../../types';

export function CompanionsPage() {
  const [showModal, setShowModal] = useState(false);
  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const { companions, loading, error, deleteCompanion } = useApp();

  const handleAddCompanion = () => {
    setSelectedCompanion(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditCompanion = (companion: Companion) => {
    setSelectedCompanion(companion);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteCompanion = async (id: number) => {
    if (window.confirm('Tem certeza que deseja remover este acompanhante?')) {
      try {
        await deleteCompanion(id);
      } catch (error) {
        console.error('Erro ao remover acompanhante:', error);
      }
    }
  };

  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  if (loading.companions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" customText="Carregando seus acompanhantes..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Meus Acompanhantes</h1>
            <p className="text-gray-600">
              Gerencie seus acompanhantes salvos para facilitar futuras viagens
            </p>
          </div>
          
          <button
            onClick={handleAddCompanion}
            className="mt-4 sm:mt-0 inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <Plus className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">Adicionar Acompanhante</span>
          </button>
        </div>

      {/* Error State */}
      {error.companions && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error.companions}</p>
        </div>
      )}

      {/* Empty State */}
      {companions.length === 0 && !loading.companions && (
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6">
            <Users className="h-12 w-12 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum acompanhante cadastrado</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Adicione acompanhantes para facilitar o processo de criação de viagens. 
            Seus dados serão salvos para uso futuro.
          </p>
          <button
            onClick={handleAddCompanion}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <Plus className="h-5 w-5 sm:mr-2" />
            <span className="hidden sm:inline">Adicionar Primeiro Acompanhante</span>
          </button>
        </div>
      )}

      {/* Companions Grid */}
      {companions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companions.map((companion) => (
            <div
              key={companion.id}
              className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-xl hover:scale-105"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">
                        {companion.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{companion.name}</h3>
                      <p className="text-sm text-gray-500">
                        {calculateAge(companion.date_of_birth)} anos
                      </p>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">CPF</p>
                    <p className="text-sm text-gray-900 font-mono">
                      {companion.document_number.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Data de Nascimento</p>
                    <p className="text-sm text-gray-900">{formatDate(companion.date_of_birth)}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Email</p>
                    <p className="text-sm text-gray-900 truncate">{companion.email || '-'}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Telefone</p>
                    <p className="text-sm text-gray-900">
                      {companion.phone ? `${companion.country_code || '+55'} ${companion.phone}` : '-'}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handleEditCompanion(companion)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDeleteCompanion(companion.id)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors duration-200"
                  >
                    Remover
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

        {/* Modal */}
        <CompanionModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          companion={selectedCompanion}
          mode={modalMode}
        />
      </div>
    </div>
  );
}

export default CompanionsPage;
